import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Shows an install prompt banner when the browser dispatches
 * `beforeinstallprompt` (Chrome/Edge/Android). iOS shows a static hint.
 */
export const InstallPWABanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("tf_pwa_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [isIOS] = useState(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  });
  const [standalone] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
    );
  });

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => setDeferred(null);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone || dismissed) return null;

  // Chrome/Android/Edge native prompt available
  if (deferred) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-40 bg-[#E5FF00] brutal-border brutal-shadow p-4 flex items-start gap-3"
      >
        <Download className="w-6 h-6 shrink-0 mt-1" strokeWidth={3} />
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase text-base leading-tight">
            Install TimeFlash
          </div>
          <div className="font-mono text-[11px] uppercase text-neutral-700 mt-1">
            Add to your home screen so alerts stay one tap away.
          </div>
          <div className="mt-3 flex gap-2">
            <button
              data-testid="pwa-install-accept"
              onClick={async () => {
                deferred.prompt();
                try {
                  await deferred.userChoice;
                } catch {}
                setDeferred(null);
              }}
              className="bg-black text-white brutal-border font-display uppercase text-sm px-3 py-2 brutal-shadow-sm brutal-interactive"
            >
              Install →
            </button>
            <button
              data-testid="pwa-install-dismiss"
              onClick={() => {
                try {
                  localStorage.setItem("tf_pwa_dismissed", "1");
                } catch {}
                setDismissed(true);
              }}
              className="bg-white brutal-border font-mono uppercase text-xs px-3 py-2 brutal-shadow-sm brutal-interactive"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={() => {
            try {
              localStorage.setItem("tf_pwa_dismissed", "1");
            } catch {}
            setDismissed(true);
          }}
          className="shrink-0"
          aria-label="Dismiss"
          data-testid="pwa-install-close"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>
      </div>
    );
  }

  // iOS static hint (no native prompt available)
  if (isIOS) {
    return (
      <div
        data-testid="pwa-install-banner-ios"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-40 bg-white brutal-border brutal-shadow p-4 flex items-start gap-3"
      >
        <Download className="w-6 h-6 shrink-0 mt-1" strokeWidth={3} />
        <div className="flex-1">
          <div className="font-display uppercase text-base leading-tight">
            Install on iPhone
          </div>
          <div className="font-mono text-[11px] uppercase text-neutral-700 mt-1">
            Tap <span className="font-bold">Share</span> → <span className="font-bold">Add to Home Screen</span>.
          </div>
        </div>
        <button
          onClick={() => {
            try {
              localStorage.setItem("tf_pwa_dismissed", "1");
            } catch {}
            setDismissed(true);
          }}
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>
      </div>
    );
  }

  return null;
};

export default InstallPWABanner;
